import { z } from 'zod';
import { meterSearchSchema } from './meter-search.ts';
import { customerSearchSchema, customerSearchSchemaWithoutRevenue } from './customer-search.ts';
import { propertySearchSchema } from './property-search.ts';
import { customGroupSearchSchema } from './custom-group-search.ts';
import { endConsumerSearchSchema } from './end-consumer-search.ts';
import { userSearchSchema } from './user-search.ts';
import { checkPermission, Permission } from './permissions.ts';

/**
 * Response contract for Smart Search's natural-language translator
 * (`POST /search/translate`, MET-3505), consumed by the search dialog.
 *
 * Every filter names the entity it searches, and `filter` is that entity's
 * spec. A new entity adds one entry to `SEARCH_ENTITIES` and one `*Filter`
 * member below; the response shape doesn't change.
 */

export const SEARCH_ENTITIES = [
  'meters',
  'customers',
  'properties',
  'customGroups',
  'endConsumers',
  'users',
] as const;
export const searchEntitySchema = z.enum(SEARCH_ENTITIES);
export type SearchEntity = z.infer<typeof searchEntitySchema>;

/**
 * The permission each entity's results endpoint requires. The translator only
 * offers (and only accepts) entities the caller holds, so it never suggests a
 * search the caller can't open, and never reveals that user search exists to
 * callers without `VIEW_USERS`.
 */
export const SEARCH_ENTITY_PERMISSIONS: Record<SearchEntity, Permission> = {
  meters: Permission.VIEW_METER_INSPECTOR,
  customers: Permission.VIEW_CUSTOMERS,
  properties: Permission.VIEW_CUSTOMERS,
  customGroups: Permission.VIEW_CUSTOM_GROUPS,
  endConsumers: Permission.MANAGE_END_CONSUMERS,
  users: Permission.VIEW_USERS,
};

/** The entities these groups may search, in `SEARCH_ENTITIES` order. */
export function searchableEntities(userGroups: readonly string[]): SearchEntity[] {
  return SEARCH_ENTITIES.filter((e) =>
    checkPermission([...userGroups], SEARCH_ENTITY_PERMISSIONS[e])
  );
}

/** Kinds of named thing the translator can resolve to an ID. */
export const RESOLVABLE_TYPES = ['customer', 'property', 'sensorGroup'] as const;
export const resolvableTypeSchema = z.enum(RESOLVABLE_TYPES);
export type ResolvableType = z.infer<typeof resolvableTypeSchema>;

export const searchCandidateSchema = z.object({
  id: z.number().int().positive(),
  label: z.string().min(1),
  type: resolvableTypeSchema,
});
export type SearchCandidate = z.infer<typeof searchCandidateSchema>;

// One member per entity: `entity` picks which spec validates `filter`.
const meterFilter = z.object({ entity: z.literal('meters'), filter: meterSearchSchema });
const customerFilter = z.object({ entity: z.literal('customers'), filter: customerSearchSchema });
const propertyFilter = z.object({ entity: z.literal('properties'), filter: propertySearchSchema });
const customGroupFilter = z.object({
  entity: z.literal('customGroups'),
  filter: customGroupSearchSchema,
});
const endConsumerFilter = z.object({
  entity: z.literal('endConsumers'),
  filter: endConsumerSearchSchema,
});
const userFilter = z.object({ entity: z.literal('users'), filter: userSearchSchema });

export const entityFilterSchema = z.discriminatedUnion('entity', [
  meterFilter,
  customerFilter,
  propertyFilter,
  customGroupFilter,
  endConsumerFilter,
  userFilter,
]);
export type EntityFilter = z.infer<typeof entityFilterSchema>;

const ENTITY_SPECS: Record<SearchEntity, z.ZodType> = {
  meters: meterSearchSchema,
  customers: customerSearchSchema,
  properties: propertySearchSchema,
  customGroups: customGroupSearchSchema,
  endConsumers: endConsumerSearchSchema,
  users: userSearchSchema,
};

/**
 * JSON-Schema source for the translator's `emit_filter` tool, limited to
 * `entities`. Tool inputs must be a plain object at the top level, so `filter`
 * is a union here and the entity/filter pairing is enforced by
 * `entityFilterSchema` on validation. Customer revenue fields are offered only
 * when `canViewRevenue` (VIEW_REVENUE); the default leaves them out.
 */
export function emitFilterToolSchemaFor(
  entities: readonly SearchEntity[],
  { canViewRevenue = false }: { canViewRevenue?: boolean } = {},
) {
  const specs = entities.map((e) =>
    e === 'customers' && !canViewRevenue ? customerSearchSchemaWithoutRevenue : ENTITY_SPECS[e]
  );
  return z.object({
    entity: z.enum(entities as [SearchEntity, ...SearchEntity[]]),
    filter: specs.length === 1
      ? specs[0]
      : z.union(specs as [z.ZodType, z.ZodType, ...z.ZodType[]]),
  });
}

/** The `emit_filter` schema covering every entity and field. */
export const emitFilterToolSchema = emitFilterToolSchemaFor(SEARCH_ENTITIES, {
  canViewRevenue: true,
});

// The three outcomes without their `kind` tag — also the translator's
// terminal tool inputs, so the tool schema and the response can't drift.
export const clarifyTranslationSchema = z.object({
  question: z.string().min(1),
  candidates: z.array(searchCandidateSchema).min(1),
});

export const unsupportedTranslationSchema = z.object({
  reason: z.string().min(1),
  partial: entityFilterSchema.optional(),
});

// Members spread the same `.shape`s that define the translator's tool inputs;
// the filter member is itself discriminated on `entity`.
export const searchTranslationSchema = z.discriminatedUnion('kind', [
  z.discriminatedUnion('entity', [
    meterFilter.extend({ kind: z.literal('filter') }),
    customerFilter.extend({ kind: z.literal('filter') }),
    propertyFilter.extend({ kind: z.literal('filter') }),
    customGroupFilter.extend({ kind: z.literal('filter') }),
    endConsumerFilter.extend({ kind: z.literal('filter') }),
    userFilter.extend({ kind: z.literal('filter') }),
  ]),
  z.object({ kind: z.literal('clarify'), ...clarifyTranslationSchema.shape }),
  z.object({ kind: z.literal('unsupported'), ...unsupportedTranslationSchema.shape }),
]);
export type SearchTranslation = z.infer<typeof searchTranslationSchema>;

export const searchTranslateRequestSchema = z.object({
  query: z.string().trim().min(1).max(500),
});
export type SearchTranslateRequest = z.infer<typeof searchTranslateRequestSchema>;
